import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Send, Loader2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { formatDate } from '../../crmConstants';

const API = process.env.REACT_APP_BACKEND_URL;

export const DealEmailTab = ({ deal, dealId, msStatus, dealEmails, onRefresh }) => {
  const [emailData, setEmailData] = useState({ subject: '', body: '' });
  const [sending, setSending] = useState(false);

  const sendEmail = async () => {
    if (!deal?.email || !emailData.subject.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/emails/send`, {
        deal_id: dealId,
        to: [deal.email],
        subject: emailData.subject,
        body: emailData.body,
        is_html: false
      }, { withCredentials: true });
      setEmailData({ subject: '', body: '' });
      onRefresh();
    } catch (err) {
      console.error('Failed to send email:', err);
      alert(err.response?.data?.detail || 'Failed to send email. Make sure your Microsoft account is connected.');
    }
    setSending(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {msStatus?.connected ? (
        <div style={{ background: '#FAFBFC', borderRadius: 8, padding: '1rem', border: '1px solid #E5E7EB' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
            <h4 style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', margin: 0 }}>
              Send Email to {deal.name}
            </h4>
            <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
              From: {msStatus.email}
            </span>
          </div>
          <Input
            value={emailData.subject}
            onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
            placeholder="Subject"
            data-testid="deal-email-subject"
            style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)', marginBottom: '0.5rem' }}
          />
          <textarea
            value={emailData.body}
            onChange={(e) => setEmailData({ ...emailData, body: e.target.value })}
            placeholder="Type your message..."
            rows={4}
            style={{
              width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #E5E7EB',
              fontSize: '0.875rem', color: 'var(--navy)', resize: 'vertical', fontFamily: 'var(--font-body)',
              marginBottom: '0.5rem'
            }}
          />
          <Button
            onClick={sendEmail}
            disabled={sending || !emailData.subject.trim()}
            className="btn-primary"
            data-testid="deal-send-email-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
            Send to {deal.email}
          </Button>
        </div>
      ) : (
        <div style={{
          background: '#FEF3C7', borderRadius: 8, padding: '1rem', border: '1px solid #F59E0B',
          textAlign: 'center'
        }}>
          <Mail size={24} style={{ color: '#F59E0B', marginBottom: '0.5rem' }} />
          <p style={{ fontSize: '0.813rem', color: '#92400E', margin: 0 }}>
            Connect your Microsoft email in the Email tab to send emails directly from deals.
          </p>
        </div>
      )}

      {/* Email History */}
      <div>
        <h4 style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
          Email History
        </h4>
        {dealEmails.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#9CA3AF', padding: '1rem', fontSize: '0.813rem' }}>
            No emails sent to this deal yet
          </p>
        ) : dealEmails.map((email, i) => (
          <div key={email.id || `email-${i}`} style={{
            padding: '0.75rem', borderRadius: 6, background: '#fff', border: '1px solid #E5E7EB',
            marginBottom: '0.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
              <span style={{
                fontSize: '0.65rem', fontWeight: 500, padding: '2px 6px', borderRadius: 100,
                background: email.direction === 'outgoing' ? '#ECFDF5' : '#EFF6FF',
                color: email.direction === 'outgoing' ? '#059669' : '#3B82F6'
              }}>
                {email.direction === 'outgoing' ? 'Sent' : 'Received'}
              </span>
              <span style={{ fontSize: '0.65rem', color: '#9CA3AF' }}>
                {formatDate(email.sent_at || email.created_at, true)}
              </span>
            </div>
            <div style={{ fontWeight: 500, fontSize: '0.813rem', color: 'var(--navy)', marginBottom: '0.25rem' }}>
              {email.subject}
            </div>
            {email.body_preview && (
              <div style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                {email.body_preview.substring(0, 150)}...
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
